import React, { useState } from 'react';
import { Panel, RadioGroup, Radio, SelectPicker, Message, useToaster, Input, Button } from 'rsuite';
import { isMobile } from 'react-device-detect';

import NavigationBar from '../Shared/NavigationBar';
import { useAuth } from '../Auth/AuthContext';
import authFetch from '../../helpers/authFetch';
import { usePreferences } from '../Preferences/PreferencesContext';
import { getThemeColors } from '../../helpers/themeColors';
import { DECK_TYPE_OPTIONS } from '../Collections/CollectionTypeSelectors';
import { VIEW_MODES, VIEW_MODE_DISPLAY } from '../../helpers/viewModeConstants';

const USERNAME_MIN = 2;
const USERNAME_MAX = 50;

const SPACING = {
  containerMaxWidth: 700,
  containerPadding: 24,
  containerPaddingMobile: 12,
  headerMarginTop: 40,
  headerMarginBottom: 32,
  sectionMarginBottom: 28,
  sectionPadding: 20,
  sectionBorderRadius: 8,
  sectionLabelMarginBottom: 12,
  controlMarginTop: 8,
  loginMessageMarginTop: 24,
};

const FONT = {
  title: 28,
  titleMobile: 22,
  subtitle: 14,
  sectionLabel: 16,
  sectionLabelWeight: 600,
  description: 13,
  loginMessage: 14,
};

const TOAST_DURATION = 2000;

const DECK_FORMAT_PICKER_DATA = [
  { label: 'None', value: '__none__' },
  ...DECK_TYPE_OPTIONS,
];

const VISIBILITY_OPTIONS = [
  { label: 'Private', value: 'PRIVATE' },
  { label: 'Invite Only', value: 'INVITE_ONLY' },
  { label: 'Public', value: 'PUBLIC' },
];

const Settings = () => {
  const { user, isAuthenticated, updateUser } = useAuth();
  const { preferences, updatePreference } = usePreferences();
  const t = getThemeColors(preferences.theme);
  const toaster = useToaster();

  const [usernameInput, setUsernameInput] = useState(user?.username || '');
  const [usernameError, setUsernameError] = useState('');
  const [usernameLoading, setUsernameLoading] = useState(false);

  const showToast = (label) => {
    toaster.push(
      <Message type="success" showIcon closable duration={TOAST_DURATION}>
        {label} updated
      </Message>,
      { placement: 'topCenter', duration: TOAST_DURATION }
    );
  };

  const handleThemeChange = (value) => {
    updatePreference('theme', value);
    showToast('Theme');
  };

  const handleVisibilityChange = (value) => {
    updatePreference('default_deck_visibility', value);
    showToast('Default visibility');
  };

  const handleDeckFormatChange = (value) => {
    updatePreference('default_deck_format', value === '__none__' ? null : value);
    showToast('Default deck format');
  };

  const handleDisplaySizeChange = (value) => {
    updatePreference('card_display_size', value);
    showToast('Card display size');
  };

  const handleDisplayModeChange = (value) => {
    updatePreference('card_display_mode', value);
    showToast('Display mode');
  };

  const handleUsernameSave = async () => {
    setUsernameError('');
    const trimmed = usernameInput.trim();

    if (!trimmed) {
      setUsernameError('Display name is required');
      return;
    }
    if (trimmed.length < USERNAME_MIN || trimmed.length > USERNAME_MAX) {
      setUsernameError(`Display name must be between ${USERNAME_MIN} and ${USERNAME_MAX} characters`);
      return;
    }

    setUsernameLoading(true);
    try {
      const res = await authFetch('/auth/me', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: trimmed }),
      });

      const data = await res.json();

      if (!res.ok) {
        setUsernameError(data.error || 'Failed to update display name');
        return;
      }

      updateUser(data.token);
      showToast('Display name');
    } catch {
      setUsernameError('Unable to connect to server');
    } finally {
      setUsernameLoading(false);
    }
  };

  const sectionStyle = {
    backgroundColor: t.cardBackground,
    border: `1px solid ${t.borderPrimary}`,
    borderRadius: SPACING.sectionBorderRadius,
    padding: SPACING.sectionPadding,
    marginBottom: SPACING.sectionMarginBottom,
  };

  const sectionLabelStyle = {
    fontSize: FONT.sectionLabel,
    fontWeight: FONT.sectionLabelWeight,
    color: t.textPrimary,
    marginBottom: SPACING.sectionLabelMarginBottom,
  };

  const descriptionStyle = {
    fontSize: FONT.description,
    color: t.textMuted,
    marginBottom: SPACING.controlMarginTop,
  };

  return (
    <>
      <NavigationBar />
      <div style={{
        maxWidth: SPACING.containerMaxWidth,
        margin: '0 auto',
        padding: isMobile ? SPACING.containerPaddingMobile : SPACING.containerPadding,
      }}>
        <div style={{
          marginTop: SPACING.headerMarginTop,
          marginBottom: SPACING.headerMarginBottom,
        }}>
          <h1 style={{
            fontSize: isMobile ? FONT.titleMobile : FONT.title,
            color: t.textPrimary,
            marginBottom: 4,
          }}>
            Settings
          </h1>
          <p style={{ fontSize: FONT.subtitle, color: t.textMuted, margin: 0 }}>
            Manage your preferences
          </p>
        </div>

        {/* Theme */}
        <div style={sectionStyle}>
          <div style={sectionLabelStyle}>Theme</div>
          <div style={descriptionStyle}>Choose your preferred color scheme</div>
          <RadioGroup
            inline={!isMobile}
            appearance="picker"
            value={preferences.theme}
            onChange={handleThemeChange}
          >
            <Radio value="dark">Dark</Radio>
            <Radio value="light">Light</Radio>
          </RadioGroup>
        </div>

        {isAuthenticated ? (
          <>
            {/* Display Name */}
            <div style={sectionStyle}>
              <div style={sectionLabelStyle}>Display Name</div>
              <div style={descriptionStyle}>This name is shown to other users instead of your email address</div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', flexDirection: isMobile ? 'column' : 'row' }}>
                <Input
                  value={usernameInput}
                  onChange={setUsernameInput}
                  placeholder="Enter a display name"
                  style={{ width: isMobile ? '100%' : 240 }}
                />
                <Button
                  appearance="primary"
                  loading={usernameLoading}
                  onClick={handleUsernameSave}
                  style={{ whiteSpace: 'nowrap' }}
                >
                  Save
                </Button>
              </div>
              {usernameError && (
                <Message type="error" showIcon style={{ marginTop: 8 }}>
                  {usernameError}
                </Message>
              )}
            </div>

            {/* Default Deck Visibility */}
            <div style={sectionStyle}>
              <div style={sectionLabelStyle}>Default Deck Visibility</div>
              <div style={descriptionStyle}>New collections will use this visibility by default</div>
              <RadioGroup
                inline={!isMobile}
                appearance="picker"
                value={preferences.default_deck_visibility}
                onChange={handleVisibilityChange}
              >
                {VISIBILITY_OPTIONS.map((opt) => (
                  <Radio key={opt.value} value={opt.value}>{opt.label}</Radio>
                ))}
              </RadioGroup>
            </div>

            {/* Default Deck Format */}
            <div style={sectionStyle}>
              <div style={sectionLabelStyle}>Default Deck Format</div>
              <div style={descriptionStyle}>New decks will use this format by default</div>
              <SelectPicker
                data={DECK_FORMAT_PICKER_DATA}
                value={preferences.default_deck_format || '__none__'}
                onChange={handleDeckFormatChange}
                searchable={false}
                cleanable={false}
                block={isMobile}
                style={{ width: isMobile ? '100%' : 200 }}
              />
            </div>

            {/* Card Display */}
            <div style={sectionStyle}>
              <div style={sectionLabelStyle}>Card Display</div>

              <div style={{ marginBottom: SPACING.sectionLabelMarginBottom }}>
                <div style={descriptionStyle}>Image size</div>
                <RadioGroup
                  inline={!isMobile}
                  appearance="picker"
                  value={preferences.card_display_size}
                  onChange={handleDisplaySizeChange}
                >
                  <Radio value="small">Small</Radio>
                  <Radio value="normal">Normal</Radio>
                  <Radio value="large">Large</Radio>
                </RadioGroup>
              </div>

              <div>
                <div style={descriptionStyle}>Display mode</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <Button
                    title={VIEW_MODE_DISPLAY[VIEW_MODES.TABLE].label}
                    appearance={preferences.card_display_mode === VIEW_MODES.TABLE ? 'primary' : 'default'}
                    onClick={() => handleDisplayModeChange(VIEW_MODES.TABLE)}
                    style={{ fontSize: 16, padding: '8px 12px', minWidth: 44, minHeight: 44 }}
                  >
                    {VIEW_MODE_DISPLAY[VIEW_MODES.TABLE].icon}
                  </Button>
                  <Button
                    title={VIEW_MODE_DISPLAY[VIEW_MODES.VISUAL].label}
                    appearance={preferences.card_display_mode === VIEW_MODES.VISUAL ? 'primary' : 'default'}
                    onClick={() => handleDisplayModeChange(VIEW_MODES.VISUAL)}
                    style={{ fontSize: 16, padding: '8px 12px', minWidth: 44, minHeight: 44 }}
                  >
                    {VIEW_MODE_DISPLAY[VIEW_MODES.VISUAL].icon}
                  </Button>
                </div>
              </div>
            </div>
          </>
        ) : (
          <Panel bordered style={{ marginTop: SPACING.loginMessageMarginTop }}>
            <p style={{ fontSize: FONT.loginMessage, color: t.textMuted, margin: 0 }}>
              Log in to access additional settings: default deck visibility, default deck format, and card display preferences.
            </p>
          </Panel>
        )}
      </div>
    </>
  );
};

export default Settings;
